import React, { useRef, useState } from 'react';
import { TFunction } from 'i18next';
import { VALID_IMAGE_EXTENSIONS } from '@/utils/image.utils';

interface useUploadDroppedFileProps {
  t: TFunction<undefined>;
  allowedFormats?: string[];
  minimumImageWidth?: string;
  minimumImageHeight?: string;
  maximumImageWidth?: string;
  maximumImageHeight?: string;
  maxImageSize?: number; // Size in MB, defaults to 2MB
  maxSizeText?: string;
  multiple?: boolean;
}

export const useUploadDroppedFile = ({
  t,
  allowedFormats = VALID_IMAGE_EXTENSIONS,
  minimumImageWidth,
  minimumImageHeight,
  maximumImageWidth,
  maximumImageHeight,
  maxImageSize = 2,
  maxSizeText,
  multiple = false,
}: useUploadDroppedFileProps) => {
  const [uploadedImage, setUploadedImage] = useState('');
  const [uploadedImages, setUploadedImages] = useState<string[]>([]);
  const [errorMessage, setErrorMessage] = useState('');
  const chooseFileInputRef = useRef<HTMLInputElement | null>(null);

  /**
   * This function is used to ensure the error message state triggers everytime it should,
   * even though the same state is used again
   */
  const showErrorMessage = (message: string) => {
    // Reset error messages state
    setErrorMessage('');
    // Use setTimeout to ensure the state update happens in the next event loop
    setTimeout(() => {
      setErrorMessage(message);
    }, 0);
  };

  const isFormatAllowed = (file: File) => {
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    if (!fileExtension || !allowedFormats.includes(fileExtension)) {
      const formatsText = allowedFormats.join(
        `, ${t('common:or').toLowerCase()} `
      );
      showErrorMessage(
        `${t('common:formatNotAllowedHereIsTheAllowedFormat')} : ${formatsText}`
      );
      return false;
    }
    return true;
  };

  const MAX_FILE_SIZE_MB = maxImageSize;

  const handleFileValidation = (file: File) => {
    setErrorMessage('');

    if (!isFormatAllowed(file)) {
      return false;
    }

    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      showErrorMessage(maxSizeText);
      return false;
    }

    if (
      minimumImageWidth ||
      minimumImageHeight ||
      maximumImageWidth ||
      maximumImageHeight
    ) {
      const img = new Image();
      img.src = URL.createObjectURL(file);

      return new Promise<boolean>((resolve) => {
        img.onload = () => {
          URL.revokeObjectURL(img.src);

          if (
            (minimumImageWidth && img.width < parseInt(minimumImageWidth)) ||
            (minimumImageHeight && img.height < parseInt(minimumImageHeight))
          ) {
            showErrorMessage(
              `${t(
                'common:errorPngImgMinDimensions'
              )} ${minimumImageWidth}px x ${minimumImageHeight}px`
            );
            resolve(false);
            return;
          }

          if (
            (maximumImageWidth && img.width > parseInt(maximumImageWidth)) ||
            (maximumImageHeight && img.height > parseInt(maximumImageHeight))
          ) {
            showErrorMessage(
              `${t(
                'common:errorPngImgMaxDimensions'
              )} ${maximumImageWidth}px x ${maximumImageHeight}px`
            );
            resolve(false);
            return;
          }

          resolve(true);
        };

        img.onerror = () => {
          URL.revokeObjectURL(img.src);
          showErrorMessage(t('common:imageIsIncorrect'));
          resolve(false);
        };
      });
    }

    return true;
  };

  const handleFileDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const handleFileDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const files = Array.from(event.dataTransfer.files).filter((file) =>
      file.type.startsWith('image/')
    );

    if (multiple) {
      handleMultipleFiles(files);
    } else {
      const file = files[0];
      if (file) {
        const validationResult = handleFileValidation(file);
        if (validationResult instanceof Promise) {
          validationResult.then((isValid) => {
            if (isValid) {
              setUploadedImage(URL.createObjectURL(file));
            }
          });
        } else if (validationResult) {
          setUploadedImage(URL.createObjectURL(file));
        }
      }
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []).filter((file) =>
      file.type.startsWith('image/')
    );

    if (multiple) {
      handleMultipleFiles(files);
    } else {
      const file = files[0];
      if (file) {
        const validationResult = handleFileValidation(file);
        if (validationResult instanceof Promise) {
          validationResult.then((isValid) => {
            if (isValid) {
              setUploadedImage(URL.createObjectURL(file));
            }
          });
        } else if (validationResult) {
          setUploadedImage(URL.createObjectURL(file));
        }
      }
    }
  };

  const handleMultipleFiles = async (files: File[]) => {
    if (files.length === 0) return;

    const validFiles: string[] = [];

    for (const file of files) {
      const validationResult = handleFileValidation(file);
      if (validationResult instanceof Promise) {
        const isValid = await validationResult;
        if (isValid) {
          validFiles.push(URL.createObjectURL(file));
        }
      } else if (validationResult) {
        validFiles.push(URL.createObjectURL(file));
      }
    }

    if (validFiles.length > 0) {
      setUploadedImages((prev) => [...prev, ...validFiles]);
    }
  };

  const removeImage = (index: number) => {
    setUploadedImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleClickChooseImage = () => {
    chooseFileInputRef.current?.click();
  };

  return {
    uploadedImage,
    uploadedImages,
    errorMessage,
    chooseFileInputRef,
    setUploadedImage,
    setUploadedImages,
    removeImage,
    handleFileDragOver,
    handleFileDrop,
    handleFileChange,
    handleClickChooseImage,
  };
};
